package com.fireavert.administration.yardi_integration_page.yardi_data_import_section.client.presentation

import com.fireavert.administration.yardi_integration_page.presentation.ImportResults
import com.fireavert.administration.yardi_integration_page.presentation.YardiPropertyImportScreen
import com.fireavert.administration.yardi_integration_page.presentation.YardiPropertyImportViewState
import com.fireavert.common.ViewModel
import com.fireavert.yardi.common_data.data_models.responses.YardiImportUnitDataResponse
import com.fireavert.yardi.shared.logic.data_models.YardiMiniCredentials
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * ViewModel for Yardi Property Import functionality
 * Manages state for the entire import process across all stepper steps
 */
class YardiPropertyImportViewModel : YardiPropertyImportScreen, ViewModel() {
    private val _viewState = MutableStateFlow(YardiPropertyImportViewState())
    val viewState = _viewState.asStateFlow()

    val scope: CoroutineScope = viewModelScope

    override var loading: Boolean
        get() = viewState.value.loading
        set(value) {
            _viewState.value = viewState.value.copy(loading = value)
        }

    override var error: String
        get() = viewState.value.error
        set(value) {
            _viewState.value = viewState.value.copy(error = value)
        }

    // Step 1: Property Selection
    override var propertyList: Map<String, Int>
        get() = viewState.value.propertyList
        set(value) {
            _viewState.value = viewState.value.copy(propertyList = value)
        }

    override var availableCredentials: List<YardiMiniCredentials>
        get() = viewState.value.availableCredentials
        set(value) {
            _viewState.value = viewState.value.copy(availableCredentials = value)
        }

    override var yardiPropertyList: List<String>
        get() = viewState.value.yardiPropertyList
        set(value) {
            _viewState.value = viewState.value.copy(yardiPropertyList = value)
        }

    // Step 2: Parsing Method
    override var yardiUnitNames: List<String>
        get() = viewState.value.yardiUnitNames
        set(value) {
            _viewState.value = viewState.value.copy(yardiUnitNames = value)
        }

    override var dashboardUnitNames: List<String>
        get() = viewState.value.dashboardUnitNames
        set(value) {
            _viewState.value = viewState.value.copy(dashboardUnitNames = value)
        }

    // Step 3: Import Process
    override var importInProgress: Boolean
        get() = viewState.value.importInProgress
        set(value) {
            _viewState.value = viewState.value.copy(importInProgress = value)
        }

    override var importProgress: Float
        get() = viewState.value.importProgress
        set(value) {
            _viewState.value = viewState.value.copy(importProgress = value)
        }

    override var importStatus: String
        get() = viewState.value.importStatus
        set(value) {
            _viewState.value = viewState.value.copy(importStatus = value)
        }

    override var importId: String?
        get() = viewState.value.importId
        set(value) {
            _viewState.value = viewState.value.copy(importId = value)
        }

    // Step 4: Results
    override var importResults: ImportResults?
        get() = viewState.value.importResults
        set(value) {
            _viewState.value = viewState.value.copy(importResults = value)
        }

    var yardiImportResponse: YardiImportUnitDataResponse?
        get() = viewState.value.yardiImportResponse
        set(value) {
            _viewState.value = viewState.value.copy(yardiImportResponse = value)
        }

    /**
     * Reset the view state to initial values
     */
    fun resetState() {
        _viewState.value = YardiPropertyImportViewState()
    }

    /**
     * Clear any error messages
     */
    fun clearError() {
        error = ""
    }

    /**
     * Update import progress
     */
    fun updateImportProgress(progress: Float, status: String) {
        _viewState.value = viewState.value.copy(
            importProgress = progress,
            importStatus = status
        )
    }
}